(ns storrito.editor.music-modal.section
  "The section-selection view of the music modal, as pure Replicant:
   after a song is picked, choose which part and how long of it plays —
   duration chips plus a draggable/resizable selection window over the
   waveform, with the selected section auto-looping. Reads its state
   from `[:ui/modal :music :selected]` and emits `:music/*` data actions
   handled in ui.editor2.modal-music.

   Dragging follows the editor2 timeline pattern (see
   ui.editor2.actions/drag-start-handler): a mousedown fires a single
   `:music/drag-start` with the drag mode; its handler installs
   document-level move/up listeners that swap the store directly per
   move, so the window follows the cursor via the normal render without a
   per-move action dispatch."
  (:require [storrito.editor.music-modal.preview :as preview]
            [storrito.ui.button :as button]))

(defn section-bar
  "The selection window can be moved by dragging it (or clicking the
   bar, which centers it) and resized to a custom duration by dragging
   its edges, like Instagram's audio trimming."
  [state]
  (let [music (get-in state [:ui/modal :music])
        {:keys [track start-ms section-ms]} (:selected music)
        duration (max 1 (:duration_in_ms track))
        highlights (:highlight_start_times_in_ms track)
        previewing? (= (get-in music [:preview :track-id])
                       (:id track))
        progress-frac (when previewing?
                        (-> (- (:preview-progress-ms music 0)
                               start-ms)
                            (/ section-ms)
                            (max 0)
                            (min 1)))
        resize-handle (fn [side]
                        [:div.absolute.inset-y-0.flex.items-center.justify-center
                         {:style (merge {:width "12px"
                                         :cursor "ew-resize"
                                         :z-index 1}
                                        (case side
                                          :left {:left "-6px"}
                                          :right {:right "-6px"}))
                          :on {:mousedown [[:js/stop-propagation]
                                           [:music/drag-start
                                            {:mode (case side
                                                     :left :resize-left
                                                     :right :resize-right)}]]}}
                         [:div.rounded
                          {:style {:width "4px"
                                   :height "60%"
                                   :background-color "#d300c5"}}]])]
    [:div.js-section-bar.relative.h-12.rounded-lg.bg-gray-200.cursor-pointer.select-none
     ;; Clicking the bar centers the window on the cursor, then drags it:
     {:on {:mousedown [[:music/drag-start {:mode :bar}]]}}
     [:div.absolute.top-0.h-full.rounded-lg.border-2
      {:class "border-[#d300c5]"
       :style {:left (str (* 100 (/ start-ms duration)) "%")
               :width (str (* 100 (/ section-ms duration)) "%")
               :background-color "rgba(211, 0, 197, 0.25)"
               :cursor "grab"}
       ;; Dragging the window keeps the grabbed position under the
       ;; cursor instead of centering:
       :on {:mousedown [[:js/stop-propagation]
                        [:music/drag-start {:mode :window}]]}}
      (when progress-frac
        [:div.absolute.top-0.left-0.h-full.rounded-md.overflow-hidden
         {:style {:width (str (* 100 progress-frac) "%")
                  :background-color "rgba(211, 0, 197, 0.45)"
                  :pointer-events "none"}}])
      (resize-handle :left)
      (resize-handle :right)]
     (for [highlight highlights]
       [:div.absolute.rounded-full
        {:replicant/key (str "highlight-" highlight)
         :style {:left (str (* 100 (/ highlight duration)) "%")
                 :top "50%"
                 :width "8px"
                 :height "8px"
                 :margin-left "-4px"
                 :margin-top "-4px"
                 :background-color "#d300c5"}
         :title (str "Popular part at " (preview/format-duration highlight))
         :on {:mousedown [[:js/stop-propagation]
                          [:music/seek-highlight highlight]]}}])]))

(defn duration-chips
  [music]
  (let [{:keys [track section-ms]} (:selected music)]
    [:div.flex.justify-center.gap-2.mb-5
     (for [option (preview/section-options music track)]
       [:button.rounded-full.px-3.py-1.text-sm.border
        {:type "button"
         :replicant/key (str "section-option-" option)
         :class (if (= option section-ms)
                  preview/accent-solid
                  "border-gray-300 text-gray-700 hover:border-gray-400")
         :on {:click [[:music/set-section option]]}}
        (preview/format-section option)])]))

(defn panel
  [state]
  (let [music (get-in state [:ui/modal :music])
        {:keys [track start-ms section-ms]} (:selected music)
        duration (max 1 (:duration_in_ms track))
        highlights (:highlight_start_times_in_ms track)
        previewing? (= (get-in music [:preview :track-id])
                       (:id track))]
    [:div.mt-4
     {:replicant/on-mount [[:music/section-restart-loop]]}
     ;; The picked song sits in the same pill as the search view's
     ;; player bar, so it visually carries over into this step: a round
     ;; back button returns to the list, and the play/pause matches the
     ;; player bar (here it toggles the looping section preview):
     [:div.flex.items-center.gap-3.rounded-xl.bg-gray-200.shadow-lg.px-3.py-2.mb-4.max-w-md.mx-auto
      [:button.rounded-full.w-9.h-9.flex.items-center.justify-center.shrink-0
       {:type "button"
        :class preview/accent-round
        :title "Change song"
        :on {:click [[:music/back-to-search]]}}
       [:i.fas.fa-arrow-left]]
      (preview/cover-thumbnail track 44)
      [:div.flex-1.min-w-0
       [:div.font-semibold.truncate
        (:title track)
        (when (:is_explicit track)
          [:span.ml-1.px-1.text-xs.text-white.bg-gray-500.rounded "E"])]
       [:div.text-sm.text-gray-600.truncate
        (:display_artist track)]]
      [:button.text-2xl.text-gray-700.hover:text-gray-900.w-9.flex.justify-center.shrink-0
       {:type "button"
        :title (if previewing? "Pause" "Play")
        :on {:click [[:music/section-toggle-play]]}}
       (if previewing?
         [:i.fas.fa-pause]
         [:i.fas.fa-play])]]

     [:label.text-gray-800.block.text-base.font-bold.mb-2.text-center
      "Select the part of the song:"]
     (duration-chips music)
     (section-bar state)
     [:div.flex.justify-between.text-xs.text-gray-600.mt-1
      [:span (preview/format-duration start-ms)]
      [:span.font-medium
       (str (preview/format-section section-ms) " section")]
      [:span (preview/format-duration duration)]]
     [:p.text-xs.text-gray-500.mt-1
      (str "Drag the edges of the selection for a custom duration."
           (when (seq highlights)
             " The dots mark the most popular parts of the song."))]

     [:div.flex.justify-end.mt-4
      (button/button-primary
        {:on {:click [[:music/confirm]]}}
        (if (:editing? music)
          "Update Music"
          "Add Music"))]]))
